import { FileUp, LogOut, Plus, RefreshCw } from 'lucide-react';
import { useState } from 'react';

import { TokenGate } from '@/auth/TokenGate';
import { useAuth } from '@/auth/useAuth';
import { AddBookmarkModal } from '@/components/Modals/AddBookmarkModal';
import { ImportBookmarksModal } from '@/components/Modals/ImportBookmarksModal';
import { BookmarksTable } from '@/components/BookmarksTab/BookmarksTable';
import { FilterBar } from '@/components/BookmarksTab/FilterBar';
import { Sidebar } from '@/components/Sidebar/Sidebar';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Toaster } from '@/components/ui/sonner';
import { useBookmarks } from '@/hooks/useBookmarks';
import { useProjects } from '@/hooks/useProjects';
import { AppProvider, useApp, type ActiveTab } from '@/state/AppContext';

export default function App() {
  return (
    <AppProvider>
      <TokenGate>
        <Shell />
      </TokenGate>
      <Toaster />
    </AppProvider>
  );
}

function Shell() {
  const { signOut } = useAuth();
  const { state, refresh, setTab, selectProject } = useApp();
  const projectsQuery = useProjects(state.refreshKey);
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const filtersForAll = {
    tags: state.filters.tags,
    from: state.filters.from || undefined,
    to: state.filters.to || undefined,
    unassigned: state.filters.unassigned || undefined,
    q: state.filters.q || undefined,
    sort: state.filters.sort,
    order: state.filters.order,
  };
  const allBookmarks = useBookmarks(filtersForAll, state.refreshKey);
  const projectBookmarks = useBookmarks(
    state.selectedProjectId ? { projectId: state.selectedProjectId } : null,
    state.refreshKey,
  );

  const selectedProject = projectsQuery.flat.find((p) => p.id === state.selectedProjectId);

  return (
    <div className="min-h-screen flex flex-col">
      <header className="flex items-center justify-between px-6 py-3 border-b border-white/5">
        <h1 className="text-base font-semibold">HyperBookmarks</h1>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={refresh}
            aria-label="Refresh"
            title="Re-fetch bookmarks and projects from the API"
          >
            <RefreshCw className={allBookmarks.loading ? 'animate-spin' : undefined} />
            Refresh
          </Button>
          <Button size="sm" onClick={() => setAddOpen(true)}>
            <Plus />
            Add Bookmark
          </Button>
          <Button size="sm" variant="ghost" onClick={signOut} aria-label="Sign out">
            <LogOut />
            Sign Out
          </Button>
        </div>
      </header>

      <div className="flex-1 flex gap-4 p-4 overflow-hidden">
        <Sidebar tree={projectsQuery.tree} onChanged={refresh} />

        <main className="flex-1 flex flex-col gap-3 overflow-hidden">
          <Tabs
            value={state.activeTab}
            onValueChange={(value) => setTab(value as ActiveTab)}
            className="flex-1 flex flex-col"
          >
            <TabsList>
              <TabsTrigger value="all">All Bookmarks</TabsTrigger>
              <TabsTrigger value="project" disabled={!state.selectedProjectId}>
                Project Bookmarks
              </TabsTrigger>
            </TabsList>

            <TabsContent value="all" className="flex flex-col gap-3 flex-1 overflow-hidden">
              <FilterBar />
              <div className="flex-1 overflow-auto card-edge bg-card rounded-lg">
                {allBookmarks.loading && allBookmarks.data.length === 0 ? (
                  <div className="text-muted-foreground text-sm py-12 text-center">Loading…</div>
                ) : allBookmarks.error && allBookmarks.data.length === 0 ? (
                  <div className="text-destructive text-sm py-12 text-center">
                    {allBookmarks.error}
                  </div>
                ) : (
                  <BookmarksTable
                    bookmarks={allBookmarks.data}
                    onChanged={refresh}
                    scopeKey={`all:${JSON.stringify(filtersForAll)}`}
                    tabContext="all"
                  />
                )}
              </div>
            </TabsContent>

            <TabsContent value="project" className="flex flex-col gap-3 flex-1 overflow-hidden">
              {!selectedProject ? (
                <div className="text-muted-foreground text-sm py-12 text-center">
                  Select a project from the sidebar.
                </div>
              ) : (
                <>
                  <div className="card-edge bg-card rounded-lg px-4 py-3 flex items-center justify-between gap-3">
                    <h2 className="text-lg font-semibold truncate">{selectedProject.name}</h2>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setImportOpen(true)}
                        aria-label="Import bookmarks"
                      >
                        <FileUp />
                        Import bookmarks
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => selectProject(null)}>
                        Deselect
                      </Button>
                    </div>
                  </div>
                  <div className="flex-1 overflow-auto card-edge bg-card rounded-lg">
                    {projectBookmarks.loading && projectBookmarks.data.length === 0 ? (
                      <div className="text-muted-foreground text-sm py-12 text-center">
                        Loading…
                      </div>
                    ) : projectBookmarks.error && projectBookmarks.data.length === 0 ? (
                      <div className="text-destructive text-sm py-12 text-center">
                        {projectBookmarks.error}
                      </div>
                    ) : (
                      <BookmarksTable
                        bookmarks={projectBookmarks.data}
                        onChanged={refresh}
                        scopeKey={`project:${selectedProject.id}`}
                        tabContext="project"
                        projectId={selectedProject.id}
                      />
                    )}
                  </div>
                </>
              )}
            </TabsContent>
          </Tabs>
        </main>
      </div>

      <AddBookmarkModal open={addOpen} onClose={() => setAddOpen(false)} onCreated={refresh} />
      {selectedProject && (
        <ImportBookmarksModal
          open={importOpen}
          onClose={() => setImportOpen(false)}
          projectId={selectedProject.id}
          projectName={selectedProject.name}
          onImported={refresh}
        />
      )}
    </div>
  );
}
